import { getDatadogRumOptions, initializeDatadogRum } from "./datadog";
import { datadogProvider } from "./datadog/provider";
import { getFaroOptions } from "./faro/faroOptions";
import { expectFaroApi, faroProvider, setFaroApi } from "./faro/provider";
import { Attributes, normalizeAttributes, ObservabilityProvider } from "./provider";

export type { Attributes, AttributeValue, ObservabilityProvider } from "./provider";

// Every backend is always listed; each one no-ops until its SDK is initialized, so the env flags
// in initializeObservability alone decide which backends receive data.
const providers: ObservabilityProvider[] = [datadogProvider, faroProvider];

const startDatadog = () => {
  const datadogOptions = getDatadogRumOptions();
  if (datadogOptions) {
    initializeDatadogRum(datadogOptions);
  }
};

const startFaro = () => {
  const faroOptions = getFaroOptions();
  if (faroOptions) {
    // Buffer calls made while the chunk loads; initializeFaroRum replays them via setFaroApi.
    expectFaroApi();
    import("./faro/initialize")
      .then(({ initializeFaroRum }) => initializeFaroRum(faroOptions))
      .catch((error) => {
        setFaroApi(undefined);
        console.error("Failed to initialize Faro", error);
      });
  }
};

/**
 * Starts each backend whose env flags enable it. Each starts on its own, so one backend failing
 * to initialize doesn't stop the other (dual-run depends on both).
 */
export function initializeObservability() {
  try {
    startDatadog();
  } catch (error) {
    console.error("Failed to initialize Datadog RUM", error);
  }
  try {
    startFaro();
  } catch (error) {
    setFaroApi(undefined);
    console.error("Failed to initialize Faro", error);
  }
}

// Calls every provider even if one throws, then rethrows the first error so callers still log it.
const forEachProvider = (call: (provider: ObservabilityProvider) => void) => {
  let firstError: unknown;
  providers.forEach((provider) => {
    try {
      call(provider);
    } catch (error) {
      firstError ??= error;
    }
  });
  if (firstError !== undefined) {
    throw firstError;
  }
};

/** Entry point for observability signals. Normalize attributes once for every backend. */
export const observability = {
  event: (name: string, properties?: Record<string, unknown> | null) => {
    const attributes: Attributes = normalizeAttributes(properties);
    forEachProvider((provider) => provider.event(name, attributes));
  },
  identify: (userId: string | undefined, traits?: Record<string, unknown> | null) => {
    const attributes: Attributes = normalizeAttributes(traits);
    forEachProvider((provider) => provider.identify(userId, attributes));
  },
  reset: () => {
    forEachProvider((provider) => provider.reset());
  },
};
